import React, { createContext, useContext, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

interface NotifyOn {
  trades: boolean;
  profitLoss: boolean;
  errors: boolean;
  signals: boolean;
  marketUpdates: boolean;
  systemUpdates: boolean;
}

export interface NotificationSettings {
  emailNotifications: boolean;
  webNotifications: boolean;
  mobileNotifications: boolean;
  notifyOn: NotifyOn;
  emailFrequency: string;
  soundAlerts: boolean;
}

interface NotificationsContextType {
  notificationSettings: NotificationSettings | null;
  loadNotificationSettings: (botId: string) => Promise<void>;
  saveNotificationSettings: (botId: string, settings: NotificationSettings) => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

const defaultSettings: NotificationSettings = {
  emailNotifications: true,
  webNotifications: true,
  mobileNotifications: false,
  notifyOn: {
    trades: true,
    profitLoss: true,
    errors: true,
    signals: true,
    marketUpdates: false,
    systemUpdates: true
  },
  emailFrequency: 'instant',
  soundAlerts: true
};

// Check if network is online using Supabase client
const checkNetworkStatus = async (): Promise<boolean> => {
  if (!navigator.onLine) {
    return false;
  }
  
  try {
    // Use a lightweight Supabase operation to check connectivity
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.error('Network check error:', error);
      return false;
    }
    return true;
  } catch (error) {
    console.error('Network check failed:', error);
    return false;
  }
};

// Retry function with exponential backoff
const retryWithBackoff = async <T,>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> => {
  let retries = 0;
  
  while (true) {
    try {
      // Check network status before attempting operation
      const isOnline = await checkNetworkStatus();
      if (!isOnline) {
        throw new Error('Network connection unavailable');
      }
      
      return await operation();
    } catch (error) {
      retries++;
      if (retries >= maxRetries) {
        throw error;
      }
      
      const delay = baseDelay * Math.pow(2, retries - 1);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings | null>(null);
  const { isAuthenticated } = useAuth();

  const loadNotificationSettings = async (botId: string) => {
    try {
      if (!isAuthenticated) {
        console.log('User not authenticated, using default settings');
        setNotificationSettings(defaultSettings);
        return;
      }

      if (!botId) {
        console.log('No bot ID provided, using default settings');
        setNotificationSettings(defaultSettings);
        return;
      }

      // Check if Supabase URL is configured
      if (!import.meta.env.VITE_SUPABASE_URL) {
        throw new Error('Supabase URL is not configured');
      }

      const fetchSettings = async () => {
        const { data, error } = await supabase
          .from('notification_settings')
          .select('*')
          .eq('bot_id', botId)
          .maybeSingle();

        if (error) throw error;
        return { data, error };
      };

      const { data, error } = await retryWithBackoff(fetchSettings);

      if (error) {
        if (error.message.includes('Network connection unavailable')) {
          toast.error('Unable to connect to the server. Please check your internet connection.');
        } else if (error.message.includes('Failed to fetch')) {
          toast.error('Unable to connect to the database. Please check your internet connection.');
        } else {
          toast.error(`Error loading settings: ${error.message}`);
        }
        console.error('Error loading notification settings:', error);
        setNotificationSettings(defaultSettings);
        return;
      }

      if (data) {
        setNotificationSettings({
          emailNotifications: data.email_notifications,
          webNotifications: data.web_notifications,
          mobileNotifications: data.mobile_notifications,
          notifyOn: data.notify_on,
          emailFrequency: data.email_frequency,
          soundAlerts: data.sound_alerts
        });
      } else {
        console.log('No settings found for bot, using defaults');
        setNotificationSettings(defaultSettings);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(`Failed to load notification settings: ${errorMessage}`);
      console.error('Error loading notification settings:', error);
      setNotificationSettings(defaultSettings);
    }
  };

  const saveNotificationSettings = async (botId: string, settings: NotificationSettings) => {
    try {
      if (!isAuthenticated) {
        throw new Error('User must be authenticated to save settings');
      }

      if (!botId) {
        throw new Error('No bot ID provided');
      }

      // Check if Supabase URL is configured
      if (!import.meta.env.VITE_SUPABASE_URL) {
        throw new Error('Supabase URL is not configured');
      }

      // Check network connectivity before attempting to save
      const isOnline = await checkNetworkStatus();
      if (!isOnline) {
        throw new Error('Network connection unavailable');
      }

      const saveSettings = async () => {
        const { data: existingSettings } = await supabase
          .from('notification_settings')
          .select('id')
          .eq('bot_id', botId)
          .maybeSingle();

        const { error } = await supabase
          .from('notification_settings')
          .upsert({
            id: existingSettings?.id,
            bot_id: botId,
            email_notifications: settings.emailNotifications,
            web_notifications: settings.webNotifications,
            mobile_notifications: settings.mobileNotifications,
            notify_on: settings.notifyOn,
            email_frequency: settings.emailFrequency,
            sound_alerts: settings.soundAlerts
          });

        if (error) throw error;
        return { error };
      };

      await retryWithBackoff(saveSettings);
      setNotificationSettings(settings);
      toast.success('Notification settings saved');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      if (errorMessage.includes('Network connection unavailable')) {
        toast.error('Unable to save settings: Please check your internet connection');
      } else if (errorMessage.includes('Failed to fetch')) {
        toast.error('Unable to connect to the database. Please try again later.');
      } else {
        toast.error(`Failed to save notification settings: ${errorMessage}`);
      }
      
      console.error('Error saving notification settings:', error);
    }
  };

  return (
    <NotificationsContext.Provider value={{ 
      notificationSettings, 
      loadNotificationSettings, 
      saveNotificationSettings 
    }}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};